import {
  Grid,
  MetricItem,
  space,
  theme,
} from "@waldner-laboreinrichtungen/component-system";
import { useTranslation } from "react-i18next";
import styledComponents from "styled-components";

import { ActiveProgramDisplay } from "../../partials/active-program.tsx";
import { ExternTag } from "../../partials/extern-tag.tsx";
import {
  formatValueNode,
  getExpectedValue,
  getIncidentBadgeProps,
  isChannelOutdated,
  isExternalChannel,
} from "../../util/channel-data.tsx";
import type { ActiveProgram, Channel, Incident } from "../../view-model.ts";

const StateBox = styledComponents.div`
  background: ${theme.colorPalette.grey100};
  border-radius: 8px;
  padding: ${space(2)} ${space(3)};
  display: flex;
  align-items: center;
  gap: ${space(2)};
  white-space: nowrap;
`;

const StateValue = styledComponents.span`
  font-weight: 600;
  color: ${theme.colorPalette.blue500};
  text-transform: capitalize;
`;

export function GridStatusRow({
  activeProgram,
  unitState,
}: {
  unitState?: string;
  activeProgram?: ActiveProgram;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <Grid.Column key="state" width={3}>
        <StateBox>
          <span>{t("detail.state.title")}:</span>
          <StateValue>{unitState}</StateValue>
        </StateBox>
      </Grid.Column>
      <Grid.Column key="spacer" width={3}>
        {/* spacer */}
      </Grid.Column>
      <Grid.Column key="program" width={6}>
        {activeProgram && (
          <ActiveProgramDisplay
            activeProgram={activeProgram}
            unitState={unitState as any}
          />
        )}
      </Grid.Column>
    </>
  );
}

export function getPortValue<T extends string>(
  channel: Channel,
  allowedPorts: readonly T[]
): T | undefined {
  const port = channel.port;
  if (typeof port !== "string") {
    return undefined;
  }
  return allowedPorts.includes(port as T) ? (port as T) : undefined;
}

/**
 * Live-Raise über den Raum-Fanout des Backends (value-update.service):
 * das `IncidentChanged`-WS-Event trägt in Mount-Räumen den ALIAS-Pfad
 * `DeviceSet/<mount>/…/FunctionSet/<fn>`, die Kachel den kanonischen
 * Quellpfad. Alias-Match: gleiches Mount-Gerät + gleiches
 * `FunctionSet/<fn>`-Suffix — dieselbe Heuristik wie die WS-Value-Updates.
 * (Zwei gemountete Functions mit identischem browseName matchen dann beide,
 * bis der nächste REST-Fetch die kanonische Identität liefert.)
 */
function isMountAliasPath(incidentPath: string, channel: Channel): boolean {
  const mountDevice = channel.mountDeviceId ?? channel.id.split("/")[0];
  if (
    !channel.browsePath ||
    !incidentPath.startsWith(`DeviceSet/${mountDevice}/`)
  ) {
    return false;
  }
  const channelFn = channel.browsePath.split("/FunctionSet/").pop();
  const incidentFn = incidentPath.split("/FunctionSet/").pop();
  return Boolean(channelFn && incidentFn && channelFn === incidentFn);
}

/**
 * Find the active incident matching a channel.
 *
 * LADS incidents match by CANONICAL browsePath — both sides carry it verbatim
 * from the backend: no path reconstruction, no mount-view ambiguity (two
 * mounted functions may share a browseName, their browsePaths never collide).
 * WS events in Mount-Räumen carry the alias path instead → `isMountAliasPath`.
 * Legacy incidents without a browsePath (laboperator/CAN) fall back to the
 * old deviceId+name derivation.
 *
 * Only currently-raised alarms (incident.active === true) light up a tile.
 * Resolved/historical incidents stay in the Fehlermeldungen list but must
 * NOT colour the MetricItem — the tile mirrors the OPC-UA condition state,
 * not the operator workflow.
 */
export function getIncidentForChannel(
  channel: Channel,
  incidents?: Incident[]
): Incident | undefined {
  return (incidents ?? []).find((incident) => {
    if (incident.active === false) return false;
    if (incident.browsePath && channel.browsePath) {
      return (
        incident.browsePath === channel.browsePath ||
        isMountAliasPath(incident.browsePath, channel)
      );
    }
    const parts = incident.name.split(".");
    if (parts.length < 2) return false;
    const channelPath = `${incident.deviceId}/${parts[0]}/${parts[1]}`;
    return (
      channel.id === channelPath || channel.id.startsWith(channelPath + "/")
    );
  });
}

export function renderFunction(
  channel: Channel,
  onSelect: (c: Channel) => void,
  gridWidth = 3,
  incidents?: Incident[]
): JSX.Element {
  const incident = getIncidentForChannel(channel, incidents);
  const expected = getExpectedValue(channel);
  return (
    <Grid.Column key={channel.id} width={gridWidth}>
      <div onClick={() => onSelect(channel)}>
        <MetricItem
          empty={!channel}
          expectedValue={expected}
          incidentState={incident?.severity}
          outdated={isChannelOutdated(channel)}
          tag={isExternalChannel(channel) ? <ExternTag /> : undefined}
          title={channel.name}
          value={formatValueNode(channel)}
          {...getIncidentBadgeProps(channel, incident)}
        />
      </div>
    </Grid.Column>
  );
}
